import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardTitle,
  CCol,
  CRow,
  CFormSelect,
  CButton,
} from "@coreui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import {
  clearMetalPrice,
  fetchCategories,
  fetchSubcategories,
  metalPriceTrade,
} from "../../features/seller/sellerSlice";
import CIcon from "@coreui/icons-react";
import { cilReload } from "@coreui/icons";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MetalPriceChart = () => {
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.seller.categories.list);
  const subCategories = useSelector((state) => state.seller.subCategories.list);
  const metalPricesState = useSelector((state) => state.seller.metalPrice);
  const metalPrice = metalPricesState?.data;
  // const loading = metalPricesState?.loading;
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [subtitle, setSubtitle] = useState("");

  useEffect(() => {
    dispatch(fetchCategories())
      .unwrap()
      .then((res) => {
        if (res.length > 0) {
          setSelectedCategoryId(res[0].id); // auto-select first category
        }
      })
      .catch((err) => console.error("Category fetch failed", err));
  }, [dispatch]);

  useEffect(() => {
    if (selectedCategoryId) {
      dispatch(fetchSubcategories(selectedCategoryId))
        .unwrap()
        .catch((err) => console.error("Subcategory fetch failed", err));
    }
  }, [dispatch, selectedCategoryId]);

  useEffect(() => {
    if (!selectedCategoryId) {
      dispatch(clearMetalPrice);
      return;
    }

    dispatch(
      metalPriceTrade({
        category_id: selectedCategoryId,
        subcategory_id: selectedSubcategoryId || "",
      })
    )
      .unwrap()
      .then((res) => setSubtitle(res.message || ""))
      .catch(() => setSubtitle(""));
  }, [dispatch, selectedCategoryId, selectedSubcategoryId]);

  const getWeekdayFromDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { weekday: "short" }); // "Mon", "Tue", ...
  };

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color =
      "#" +
      ((hash >> 24) & 0xff).toString(16).padStart(2, "0") +
      ((hash >> 16) & 0xff).toString(16).padStart(2, "0") +
      ((hash >> 8) & 0xff).toString(16).padStart(2, "0");
    return color.slice(0, 7); // Ensure 6-char hex
  }

  const transparentize = (hex, opacity = 0.1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  };

  let allDates = [];
  let labels = [];
  let datasets = [];

  if (Array.isArray(metalPrice)) {
    allDates = Array.from(
      new Set(metalPrice.flatMap((metal) => metal.prices.map((p) => p.date)))
    ).sort((a, b) => new Date(a) - new Date(b));

    labels = allDates.map((date) => getWeekdayFromDate(date));

    datasets = metalPrice.map((metal) => {
      const priceMap = Object.fromEntries(
        metal.prices.map((p) => [p.date, parseFloat(p.price.replace(/,/g, ""))])
      );
      const color = stringToColor(metal.metal);
      return {
        label: metal.metal,
        data: allDates.map((date) => priceMap[date] ?? null),
        borderColor: color,
        backgroundColor: transparentize(color),
        pointRadius: 5,
        pointHoverRadius: 6,
        pointBackgroundColor: "#fff",
        pointBorderColor: color,
        tension: 0.4,
        fill: true,
      };
    });
  }
  // console.log("Data Set:", datasets);
  const metalChartData = { labels, datasets };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      x: { title: { display: true, text: "Day" } },
      y: { title: { display: true, text: "Price ($)" } },
    },
  };

  return (
    <CRow className="mt-4 mb-4">
      <CCol>
        <CCard>
          <CCardBody>
            <div className="d-flex justify-content-between align-items-end mb-3">
              <div>
                <CCardTitle className="mb-1">Metal Price Trend</CCardTitle>
                {subtitle && <small className="text-muted">{subtitle}</small>}
              </div>
              <div className="d-flex gap-2">
                <CFormSelect
                  size="sm"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  options={[
                    { label: "Select Category", value: "" },
                    ...categories.map((c) => ({
                      label: c.name,
                      value: c.id,
                    })),
                  ]}
                />
                <CFormSelect
                  size="sm"
                  value={selectedSubcategoryId}
                  onChange={(e) => setSelectedSubcategoryId(e.target.value)}
                  disabled={!selectedCategoryId}
                  options={[
                    { label: "All Subcategories", value: "" },
                    ...subCategories.map((sc) => ({
                      label: sc.name,
                      value: sc.id,
                    })),
                  ]}
                />
                <CButton
                  color="light"
                  size="sm"
                  className="p-2"
                  onClick={() => setSelectedSubcategoryId("")}
                  disabled={!selectedCategoryId}
                  title="Reset Subcategory"
                >
                  <CIcon icon={cilReload} />
                </CButton>
              </div>
            </div>

            <div
              className="chart-wrapper"
              style={{ position: "relative", width: "100%", height: "350px" }}
            >
              <Line
                data={metalChartData}
                options={{ ...chartOptions, maintainAspectRatio: false }}
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default MetalPriceChart;
